import { ErrorHandler, inject, Injector, NgZone, Service } from '@angular/core';
import { NavigationEnd, NavigationError, NavigationStart, Router, RouterEvent } from '@angular/router';

import { lastValueFrom, mergeMap, Observable, of, tap } from 'rxjs';

import {
    _addAuditInfoOnDocument,
    BaasAppState,
    BaasAuditModel,
    BaasAuditService,
    BaasAuditType,
    BaasMeetpuntService,
    BaasTelemetry,
    BaasTelemetrySpan,
    BaasToepassingService,
    BarStopwatch,
    ensureGlobal,
    generateUuid
} from '@lvflanders/bar';

import { BAR_INITIALIZED } from '../constants/internal-tokens.constants';
import { BAAS_TELEMETRY } from '../constants/tokens.constants';
import { barMeldingGuard } from '../guards/bar-melding.guard';
import { BarInitializedHook } from '../hooks/bar-initialized.hook';
import { BarMeldingenEngine } from '../providers/bar-meldingen-engine.provider';
import { _BarRouteLogger } from '../providers/bar-route-logger.provider';
import { _BarCoreStateService } from './baas-core-state.service';

/** Public BAR (initialization) service */
@Service({ autoProvided: false })
export class BarStartupService {
    readonly #injector = inject(Injector);
    readonly #audit = inject(BaasAuditService);
    readonly #state = inject(BaasAppState);
    readonly #meetpunt = inject(BaasMeetpuntService);
    readonly #initializedHooks = inject<BarInitializedHook[]>(BAR_INITIALIZED, { optional: true });
    readonly #toepassing = inject(BaasToepassingService);
    readonly #routeLogger = inject(_BarRouteLogger);
    readonly #coreState = inject(_BarCoreStateService);
    readonly #meldingEngine = inject(BarMeldingenEngine);
    readonly #zone = inject(NgZone);
    readonly #error = inject(ErrorHandler);
    readonly #telemetry = inject<BaasTelemetry>(BAAS_TELEMETRY);

    #stopwatch: BarStopwatch;
    #telemetryGlobalSpan: BaasTelemetrySpan;

    // avoid cyclic DI error
    get #router(): Router {
        return this.#injector.get(Router);
    }

    constructor() {
        // here we put some global stuff
        const core = ensureGlobal('lvflanders.bar.ng.core');
        // make global variable to handle the error from outside Angular
        core.handleError = (error) => {
            // run back in Angular zone from outside Angular
            this.#zone.run(() => {
                // invoke error routine
                this.#error.handleError(error);
            });
        };
        // handled unhanded errors with promises
        window.onunhandledrejection = (event) =>
            this.#zone.run(() => {
                this.#error.handleError(event.reason);
            });
    }

    async load(): Promise<any> {
        if (this.#telemetry.currentTransaction) {
            this.#telemetryGlobalSpan = this.#telemetry.currentTransaction.startSpan(
                'Angular startup process (post @lvflanders/bar bootstrap)',
                'ng-startup-process'
            );
        }

        this.#coreState.basiscontext.set(this.#state.basiscontext);
        // init logging of routes
        this.#routeLogger.init();
        this.#enhanceRouting();
        // meldingen and audit stuff bound to routing
        this.#bindRouterEvents();
        // here we put some global stuff
        const core = ensureGlobal('lvflanders.bar.ng.core');
        // make sure the state is available globally for non Angular code
        core.state = this.#state;
        // put the toepassing service on global scope to open it up for outside user (such as puppeteer and web accessibility)
        core.toepassing = this.#toepassing;

        return lastValueFrom(this.#initialized());
    }

    /** Invoked when BAR is initialized. */
    #initialized(): Observable<void> {
        const end = () => {
            if (this.#telemetryGlobalSpan) this.#telemetryGlobalSpan.end();
        };
        if (this.#initializedHooks != null) {
            return this.#executeInitializedHooks().pipe(tap(() => end()));
        } else {
            end();
            return of(undefined);
        }
    }

    #executeInitializedHooks(): Observable<void> {
        const hookComparer = (a: BarInitializedHook, b: BarInitializedHook) => {
            // first let _framework internal property take precedence
            if (a._framework && !b._framework) return -1;
            if (!a._framework && b._framework) return 1;
            // second take into account the order
            if (a.order == null && b.order == null) return 0;
            if (a.order != null && b.order == null) return -1;
            if (a.order == null && b.order != null) return 1;
            if (a.order < b.order) return -1;
            if (a.order > b.order) return 1;
            return 0;
        };
        // sort the hooks by order, if no order it goes in the back of the list
        const sortedHooks = this.#initializedHooks.sort(hookComparer);

        let result$: Observable<void> = null;
        // NOTE concat didn't seem to work and forkJoin doesn't preserve the order
        // ensure everything is ran sequentially by modifying the observable till we reach the end of the array
        for (const hook of sortedHooks) {
            const hookOutput$ = hook.run() || of(undefined);
            if (!result$) result$ = hookOutput$;
            else result$ = result$.pipe(mergeMap(() => hookOutput$));
        }

        return result$;
    }

    #bindRouterEvents(): void {
        // here we measure the routing time
        this.#router.events.subscribe(async (val) => {
            // on navigation start we start the stopwatch
            if (val instanceof NavigationStart) {
                // first reset the meldingen
                this.#coreState.meldingen.set(undefined);
                if (this.#stopwatch != null) {
                    this.#stopwatch.start();
                }
            }
            // on navigation end or error we stop the stopwatch
            else if (val instanceof NavigationEnd) {
                // meldingen are still undefined, meaning there was no meldingen guard, retrieve meldingen
                if (this.#coreState.meldingen() === undefined) await this.#retrieveMeldingen(val.urlAfterRedirects || val.url);
                this.#endRouteWatch(val, 'ROUTER_NAVIGATION_END');
            } else if (val instanceof NavigationError) {
                this.#endRouteWatch(val, 'ROUTER_NAVIGATION_END');
            }
        });
    }

    #endRouteWatch(val: RouterEvent, meetpunt: string) {
        // generate the route id
        const routeIdKey: keyof BaasAppState = 'routeId';
        this.#state[routeIdKey as any] = generateUuid();

        // audit this
        const audit = {
            auditId: this.#state.routeId,
            parentAuditId: this.#state.pageId,
            auditType: BaasAuditType.Route,
            details: {
                ROUTE: val.url,
                // also log the URL for completeness
                URL: document.location.href
            }
        } as BaasAuditModel;
        this.#audit.log(audit);
        _addAuditInfoOnDocument(audit);

        // do it in timeout so we executed meetpunt after (more accurate measurement)
        window.setTimeout(() => {
            // if no stopwatch it means meetpunten is not enabled
            if (this.#stopwatch != null) {
                this.#stopwatch.stop();

                this.#meetpunt.log({
                    meetpunt,
                    meetpuntDetail: val.url,
                    waarde: this.#stopwatch.duration,
                    meetpuntInfo: val.url,
                    auditId: this.#state.routeId
                });
            }
        }, 0);
    }

    /** Enhances routing of application by adding guards where needed. */
    #enhanceRouting() {
        if (this.#router.config) {
            this.#router.config
                // do not handle framework routes
                .filter((route) => route.data?._framework !== true)
                .forEach((route) => {
                    if (route.children || route.loadChildren) {
                        if (!route.canActivateChild) route.canActivateChild = [];
                        route.canActivateChild.push(barMeldingGuard());
                    } else if (route.component) {
                        if (!route.canActivate) route.canActivate = [];
                        route.canActivate.push(barMeldingGuard());
                    }
                });
        }
    }

    async #retrieveMeldingen(routeUrl: string): Promise<void> {
        await this.#meldingEngine.retrieveAndProcess(routeUrl);
    }
}
